import React, { useMemo, useRef } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const DatePickerRow = ({ selectedDate, onDateSelect, currentWeek, setCurrentWeek, restrictFutureDates = false, restrictPastDates = false }) => {
  const dateInputRef = useRef(null);

  const generateWeekDates = () => {
    const days = [];
    const startOfWeek = new Date();
    const dayOfWeek = startOfWeek.getDay();
    const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

    startOfWeek.setDate(startOfWeek.getDate() + offset + currentWeek * 7);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push(date.toISOString().split("T")[0]);
    }

    return days;
  };

  const getDynamicLabel = (date) => {
    const today = new Date().toISOString().split("T")[0];
    if (date === today) return "TODAY";
    return new Date(date).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isRestrictedDate = (date) => {
    const today = new Date().toISOString().split("T")[0];
    if (restrictFutureDates && new Date(date) > new Date(today)) return true;
    if (restrictPastDates && new Date(date) < new Date(today)) return true;
    return false;
  };

  const weekDates = useMemo(() => generateWeekDates(), [currentWeek]);

  const handleNextWeek = () => {
    if (!restrictFutureDates || new Date(weekDates[6]) < new Date()) {
      setCurrentWeek((prev) => prev + 1);
    }
  };

  const handlePreviousWeek = () => {
    if (!restrictPastDates || currentWeek > 0) {
      setCurrentWeek((prev) => prev - 1);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 2, marginLeft: -1 }}>
      <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <IconButton onClick={handlePreviousWeek} disabled={restrictPastDates && currentWeek === 0}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: 0, borderRadius: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", overflowX: "auto", gap: 1 }}>
            {weekDates.map((date, index) => {
              const isSelected = selectedDate === date;
              const isDisabled = isRestrictedDate(date);

              return (
                <Box
                  key={index}
                  onClick={() => !isDisabled && onDateSelect(date)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    gap: "2px",
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: "0.75rem", color: isSelected ? "#FF9800" : "#681517" }}>
                    {new Date(date).toLocaleDateString("en-US", { weekday: "short" }).charAt(0)}
                  </Typography>
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      border: `1px solid ${isSelected ? "#FF9800" : "#681517"}`,
                      backgroundColor: isSelected ? "#FF9800" : "transparent",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: isSelected ? "#FFF" : "#681517",
                    }}
                  >
                    {new Date(date).getDate()}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
        
        <IconButton onClick={handleNextWeek} disabled={restrictFutureDates && new Date(weekDates[6]) >= new Date()}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 2 }}>
        <IconButton>
          <CalendarTodayIcon fontSize="medium" sx={{ color: "#681517" }} />
        </IconButton>
        <Typography variant="body2" sx={{ fontSize: 14, color: "#FF9800" }}>
          {selectedDate}
        </Typography>
      </Box>
    </Box>
  );
};

export default DatePickerRow;